import { MENU_ITEMS, WHATSAPP_MENU_ITEMS } from '../assets/data/menu-items';

export const getMenuItems = () => MENU_ITEMS;

export const getWhatsAppMenuItems = () => WHATSAPP_MENU_ITEMS;

/**
 * Normalise permission codes from /admin/auth/me admin object.
 * @param {object|null|undefined} admin
 * @returns {string[]}
 */
export const getPermissionCodes = (admin) => {
  const raw = admin?.permissions || admin?.permission_codes || [];
  if (!Array.isArray(raw)) return [];
  return raw.map((p) => (typeof p === 'string' ? p : p?.code)).filter(Boolean);
};

const isSuperAdminRole = (admin) => {
  if (!admin) return false;
  const single = admin.role_code || admin.role;
  if (single === 'super_admin' || single === 'ADMIN_SUPER') return true;

  const roles = admin.roles || admin.role_codes || [];
  if (!Array.isArray(roles)) return false;
  return roles.some((r) => {
    const code = typeof r === 'string' ? r : r?.code;
    return code === 'super_admin' || code === 'ADMIN_SUPER';
  });
};

/**
 * @param {object|null|undefined} admin
 * @param {string[]|undefined} requiredCodes — any match grants access
 * @returns {boolean}
 */
export const adminHasAnyPermission = (admin, requiredCodes) => {
  if (!requiredCodes?.length) return true;
  if (!admin) return false;
  if (isSuperAdminRole(admin)) return true;
  const set = new Set(getPermissionCodes(admin));
  return requiredCodes.some((c) => set.has(c));
};

/**
 * Remove leading/trailing/duplicate dividers left after permission filtering.
 * @param {unknown[]} items
 * @returns {unknown[]}
 */
const collapseMenuDividers = (items) => {
  /** @type {unknown[]} */
  const out = [];
  for (const item of items) {
    if (item.isDivider) {
      if (!out.length || out[out.length - 1].isDivider) continue;
      out.push(item);
      continue;
    }
    out.push(item);
  }
  while (out.length && out[out.length - 1].isDivider) out.pop();
  return out;
};

/**
 * Drop menu entries the current admin cannot access (requires `requiredPermissions` on items).
 * @param {unknown[]} menuItems
 * @param {object|null|undefined} admin
 * @returns {unknown[]}
 */
export const filterMenuItemsByPermissions = (menuItems, admin) => {
  if (!menuItems?.length) return [];
  /** @type {unknown[]} */
  const out = [];
  for (const item of menuItems) {
    if (item.isDivider || item.isTitle) {
      out.push(item);
      continue;
    }
    if (item.children?.length) {
      const children = filterMenuItemsByPermissions(item.children, admin);
      if (children.length) {
        out.push({ ...item, children });
      }
      continue;
    }
    if (item.requiredPermissions && !adminHasAnyPermission(admin, item.requiredPermissions)) {
      continue;
    }
    out.push(item);
  }
  return collapseMenuDividers(out);
};

export const findMenuItem = (menuItems, menuItemKey) => {
  if (!menuItems || !menuItemKey) return null;
  for (const item of menuItems) {
    if (item.key === menuItemKey) return item;
    const found = findMenuItem(item.children, menuItemKey);
    if (found) return found;
  }
  return null;
};

export const findAllParent = (menuItems, menuItem) => {
  const parents = [];
  const parent = findMenuItem(menuItems, menuItem.parentKey);
  if (parent) {
    parents.push(parent.key);
    if (parent.parentKey) {
      parents.push(...findAllParent(menuItems, parent));
    }
  }
  return parents;
};

const flattenMenuItemsWithUrl = (items, out = []) => {
  if (!items) return out;
  const list = Array.isArray(items) ? items : [items];
  for (const item of list) {
    if (item.isDivider || item.isTitle) continue;
    if (item.url) out.push(item);
    if (item.children?.length) flattenMenuItemsWithUrl(item.children, out);
  }
  return out;
};

/**
 * Resolve the menu item for a URL. Prefers exact match, then the longest
 * prefix match so /rewards/sanduk-coins highlights Sanduk Coins, not Rewards.
 */
export const getMenuItemFromURL = (items, url) => {
  if (!url || !items) return null;

  const candidates = flattenMenuItemsWithUrl(items);
  const exact = candidates.find((item) => item.url === url);
  if (exact) return exact;

  let best = null;
  let bestLen = 0;
  for (const item of candidates) {
    if (!item.url || item.url === '/') continue;
    if (url.startsWith(`${item.url}/`) && item.url.length > bestLen) {
      best = item;
      bestLen = item.url.length;
    }
  }
  return best;
};
